import { Search } from "lucide-react";
import React, { useCallback, useEffect, useState } from "react";
import lodash from "lodash";
import { useClickOutside } from "@mantine/hooks";
import {
  Anchor,
  Breadcrumbs,
  Button,
  Divider,
  SimpleGrid,
  Space,
  TextInput,
} from "@mantine/core";
import {
  Navigate,
  useNavigate,
  useParams,
  useLocation,
} from "react-router-dom";
import PageHeading from "../../../themes/components/PageHeading";
import PageContainer from "../../../themes/components/PageContainer";
import PayslipIssuesTable from "../../payslips/components/PayslipIssuesTable";
import ProgressIndicator from "../../../themes/components/ProgressIndicator";
import ErrorPage from "../../../common/pages/ErrorPage";
import EmptyStateContainer from "../../../themes/components/EmptyStateContainer";
import DocumentImporterConfiguration from "../../payslips/services/model/DocumentImporterConfiguration";
import { RosterJourneyConfiguration } from "../RosterJourneyConfiguration";
import useUnmatchedDocumentDetailsApiService from "../../payslips/services/api/UnmatchedDocumentDetailsApiService";
import UnmatchedDocument from "../../payslips/services/model/UnmatchedDocument";
import NotFound from "../../../common/pages/NotFound";
import { AccessPermissionContext } from "../../../providers/AccessPermissionsProvider";
import { useEnabledFeatureFlag } from "../../../common/feature-flagging/FeatureFlagProvider";
import FeatureFlags from "../../../common/feature-flagging/FeatureFlags";
import { AnalyticsContext } from "../../../providers/AnalyticsProvider";

const RosterIssuesPage: React.FC = () => {
  const documentImporterConfiguration: DocumentImporterConfiguration =
    RosterJourneyConfiguration;

  const [selection, setSelection] = useState<UnmatchedDocument[]>([]);
  const navigate = useNavigate();

  const unmatchedRostersSearchBarEnabled = useEnabledFeatureFlag(
    FeatureFlags.UNMATCHED_PAYSLIPS_SEARCH_BAR
  );

  const { hasPermissions } = React.useContext(AccessPermissionContext);
  const { trackDocumentsEvent } = React.useContext(AnalyticsContext);

  const { id } = useParams();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { state }: any = useLocation();

  const previousSearchterm = state?.isSearchPresent
    ? state.previousSearchTerm
    : "";
  const [searchTerm, setSearchTerm] = useState(previousSearchterm);

  const [unmatchedRostersrequest, setUnmatchedRostersrequest] = useState({
    id: Number(id),
    search: searchTerm,
  });

  const searchUnmatchedRosters = (keyCode: string): void => {
    if (keyCode === "Enter" && searchTerm.trim().length > 0) {
      setUnmatchedRostersrequest((prevState) => ({
        ...prevState,
        search: searchTerm,
      }));

      // used to populate the search input post re loading
      state.isSearchPresent = true;
      state.previousSearchTerm = searchTerm;
    }
  };

  const searchRef = useClickOutside(() => {
    if (searchTerm.trim().length === 0) {
      state.isSearchPresent = false;
      setUnmatchedRostersrequest((prevState) => ({
        ...prevState,
        search: searchTerm,
      }));
    }
  });

  const { isLoading, isError, data, error } =
    useUnmatchedDocumentDetailsApiService({
      id: unmatchedRostersrequest.id,
      search: unmatchedRostersrequest.search,
    });

  let noResultsErrorMessage = "";

  if (data && data.documents.length === 0 && state?.isSearchPresent) {
    noResultsErrorMessage = `We couldn't find a match for "${searchTerm}"`;
  }

  const debouncedSearchTermInput = useCallback(
    lodash.debounce((searchInputValue) => {
      if (searchInputValue.length === 0) {
        return;
      }
      trackDocumentsEvent(
        {
          event: `Unmatched_${documentImporterConfiguration.document_name_plural}_Dashboard_Search`,
          description: `User clicks and/or searches on the search bar on the unmatched ${documentImporterConfiguration.document_name_plural_lower_case} dashboard`,
          event_type: "Search_Bar_Input",
          type: documentImporterConfiguration.document_name_plural,
          job: data || null,
        },
        {
          search_input: searchInputValue || "",
        }
      );
    }, 500),
    [data]
  );

  useEffect(() => {
    if (searchTerm.trim().length > 0) {
      debouncedSearchTermInput(searchTerm);
    }
  }, [searchTerm, debouncedSearchTermInput]);

  useEffect(() => {
    if (data) {
      trackDocumentsEvent(
        {
          event: `Unmatched_${documentImporterConfiguration.document_name_plural}_Dashboard`,
          description: `User views the unmatched ${documentImporterConfiguration.document_name_plural_lower_case} dashboard`,
          event_type: "PageView",
          type: documentImporterConfiguration.document_name_plural,
          job: data,
        },
        {
          notification_sent_status: data.should_send_notifications,
        }
      );
    }
  }, [data]);

  if (!id) {
    return <Navigate to="/rosters" />;
  }

  if (!hasPermissions) {
    return <NotFound />;
  }

  if (isLoading) {
    return <ProgressIndicator />;
  }

  if (isError) {
    return <ErrorPage errors={error} />;
  }

  return (
    <PageContainer>
      <PageHeading
        title={`Unmatched ${documentImporterConfiguration.document_name_plural}`}
      />
      <Space
        style={{
          paddingTop: "32px",
        }}
      />
      <SimpleGrid cols={2} style={{ alignItems: "center" }}>
        <Breadcrumbs separator=">">
          {[
            {
              title: documentImporterConfiguration.document_name_plural,
              href: "/rosters",
            },
            { title: "Error", href: "#" },
            {
              title:
                !data || !data.documents.length
                  ? `document-job-${id}`
                  : data.batch_name,
              href: "",
            },
          ].map((item) => (
            <Anchor
              style={{
                color:
                  item.title === `document-job-${id}`
                    ? "#062133"
                    : "rgba(6, 33, 51, 0.5)",
              }}
              href={item.href}
              key={`${item.title}-${item.href}`}
            >
              {item.title}
            </Anchor>
          ))}
        </Breadcrumbs>

        {unmatchedRostersSearchBarEnabled && (
          <div>
            <TextInput
              ref={searchRef}
              error={noResultsErrorMessage}
              id="se"
              size="md"
              onKeyUp={(event) => searchUnmatchedRosters(event.code)}
              onChange={(event) => {
                setSearchTerm(event.target.value);
              }}
              value={searchTerm}
              placeholder="Search"
              rightSection={
                <Button
                  color="jem-theme"
                  size="xs"
                  onClick={() => {
                    searchUnmatchedRosters("Enter");
                  }}
                >
                  <Search size={18} />
                </Button>
              }
            />
            {noResultsErrorMessage.length === 0 && state?.isSearchPresent && (
              <span style={{ lineHeight: 1.5, marginTop: "5px" }}>
                {data?.documents.length} result
                {data && data?.documents.length > 1 ? "s" : ""} found
              </span>
            )}
          </div>
        )}
      </SimpleGrid>
      <Space h="xl" />
      <Divider my="sm" />
      {!data?.documents?.length ? (
        <EmptyStateContainer
          numberOfSpaces={1}
          title={`No unmatched ${documentImporterConfiguration.document_name_plural_lower_case}`}
          description={`All of the unmatched ${documentImporterConfiguration.document_name_lower_case} issues have been resolved.`}
          button={{
            onClick: () => navigate("/rosters"),
            title: `Back to ${documentImporterConfiguration.document_name_plural}`,
          }}
        />
      ) : (
        <>
          <Space h="xl" />
          <PayslipIssuesTable
            documentImporterConfiguration={documentImporterConfiguration}
            data={data}
            selection={selection}
            onSelection={setSelection}
            jobStatus={state?.jobStatus}
          />
        </>
      )}
    </PageContainer>
  );
};

export default RosterIssuesPage;
